import { i18n } from "@lingui/core";
import { clickable } from "@warp-ds/css/component-classes";
import WarpElement from "@warp-ds/elements-core";
import "@warp-ds/icons/elements/bulb-32";
import "@warp-ds/icons/elements/calculator-32";
import "@warp-ds/icons/elements/car-service-32";
import "@warp-ds/icons/elements/chevron-right-16";
import { css, html, TemplateResult } from "lit";
import { trackClickEvent } from "../tracking.js";
import { Subvertical } from "../../constants.js";
import { TrackingEventMap, eventReference } from "../../models/trackingEvent.js";
import { activateI18n } from "../../utils/i18n.js";

interface CardWithIcon {
    icon: TemplateResult<1>;
    href: string;
    title: string;
    textStyle?: string;
    description: string;
    track?: (destination: string) => void;
}

interface Button {
    title: string;
    href: string;
    track: (destination: string) => void;
}

enum MFPRoute {
    SMIDIG_BILHANDEL = "/motor/artikler/aktuelt/smidig-bilhandel",
    CAR_CONTENT_UNIVERSE = "/motor/artikler/bil",
    CAR_LOAN_CALCULATOR = "/finance/marketplace/loan/car",
    CAR_PRICING_ADVICE = "/pristips/bruktbil",
    CAR_INSURANCE = "/finance/marketplace/insurance/car",
    CHOOSE_OWN_LOAN_OR_SUBSCRIPTION = "/eievelger",
}

type Card = CardWithIcon;

class UsefulTools extends WarpElement {
    declare baseurl: string;
    declare subVertical: Subvertical;
    declare trackingEventMap: TrackingEventMap;

    static properties = {
        baseurl: { attribute: true, type: String },
        subvertical: { attribute: true, type: Subvertical },
        trackingEventMap: { type: Object },
    };

    static styles = [
        WarpElement.styles,
        css`
            @css-placeholder-hEV4b8;

            .description-container {
                grid-template-rows: subgrid;
                grid-row: span 3;
            }
        `,
    ];

    // User-defined type guard function for CardWithIcon
    isCardWithIcon(card: Card): card is CardWithIcon {
        return "icon" in card;
    }

    cardWithIcon(card: CardWithIcon) {
        return html`
            <w-card class="grid">
                <div class="p-16 grid gap-24 relative description-container">
                    ${card.icon}
                    <div>
                        <h4 class="t4 flex">
                            <a href="${card.href}" class="s-text-primary flex-1" @click="${() => card.track(card.href)}">
                                <span class="${clickable.buttonOrLinkStretch}" aria-hidden="true"></span>
                                ${card.title}
                            </a>
                            <w-icon-chevron-right-16 class="flex items-center"></w-icon-chevron-right-16>
                        </h4>
                        <p class="text-body">${card.description}</p>
                    </div>
                </div>
            </w-card>
        `;
    }

    renderCards(cards: Card[]) {
        return html`
            <div class="flex flex-col md:grid md:grid-cols-3 gap-16 mb-56">
                ${cards.map((card: Card) => {
                    if (this.isCardWithIcon(card)) return this.cardWithIcon(card);
                    return "";
                })}
            </div>
        `;
    }

    renderButton(button: Button) {
        return html`
            <w-card>
                <a
                    href="${button.href}"
                    aria-label="${button.title}"
                    class="${clickable.buttonOrLinkStretch}"
                    @click="${() => button.track(button.href)}"
                ></a>
                <w-button variant="link" class="grid s-bg place-items-center p-16">${button.title}</w-button>
            </w-card>
        `;
    }

    renderButtons(buttons: Button[]) {
        const cols = buttons.length;
        return html`
            <div class="flex flex-col md:grid md:grid-cols-${cols} gap-16 mb-56">${buttons.map((button) => this.renderButton(button))}</div>
        `;
    }

    connectedCallback() {
        activateI18n();
        super.connectedCallback();
    }

    render() {
        const title = i18n.t({
            id: "market-frontpage.useful-tools",
            message: "Useful tools",
        });

        const cards: Card[] = [
            {
                icon: html`
                    <div class="box-border h-[100] w-[100] rounded-full grid place-items-center s-bg-warning-subtle">
                        <w-icon-bulb-32 class="s-icon flex" />
                    </div>
                `,
                href: MFPRoute.CAR_CONTENT_UNIVERSE,
                title: i18n.t({
                    id: "market-frontpage.car-guide",
                    message: "The car guide",
                }),
                description: i18n.t({
                    id: "market-frontpage.useful-vehicles-description",
                    message: "Here can you find relevant content, smart tips, tricks and good advice",
                }),
                track: (destination: string) => trackClickEvent(this.trackingEventMap[eventReference.CAR_GUIDE], destination),
            },
            {
                icon: html`
                    <div class="box-border h-[100] w-[100] rounded-full grid place-items-center s-bg-primary-subtle">
                        <w-icon-calculator-32 class="s-icon flex" />
                    </div>
                `,
                href: MFPRoute.CAR_LOAN_CALCULATOR,
                title: i18n.t({
                    id: "market-frontpage.loan-calculator",
                    message: "Loan calculator",
                }),
                description: i18n.t({
                    id: "market-frontpage.loan-calculator-description",
                    message: "Find out how much the car loan will cost at different banks.",
                }),
                track: (destination: string) => trackClickEvent(this.trackingEventMap[eventReference.LOAN_CALCULATOR], destination),
            },
            {
                title: i18n.t({
                    id: "market-frontpage.insure-your-car",
                    message: "Insure your car",
                }),
                description: i18n.t({
                    id: "market-frontpage.insure-your-car-description",
                    message: "Discover which providers can help you with car insurance.",
                }),
                href: MFPRoute.CAR_INSURANCE,
                track: (destination: string) => trackClickEvent(this.trackingEventMap[eventReference.INSURANCE], destination),
                icon: html`
                    <div class="box-border h-[100] w-[100] rounded-full grid place-items-center s-bg-positive-subtle">
                        <w-icon-car-service-32 class="s-icon flex"></w-icon-car-service-32>
                    </div>
                `,
            },
        ];

        return html`
            <h3 class="mb-16">${title}</h3>
            ${this.renderCards(cards)}
        `;
    }
}

customElements.define("useful-tools", UsefulTools);
